/**
 * ACTIVE.1 — pure Weapon×Primary source-profile resolution and the F/K
 * damage core. `weapon-primary-basic-attack-contract.md` §4-9,
 * `source-relative-active-skill-contract.md` §1-2,
 * `active-skill-mechanics-addendum-v0.1.md` §1/§1.1/§6.
 *
 * Every function here is pure and deterministic (shared production
 * formulas, no duplication) — the simulator, the
 * authoritative world, and CAL.1A all consume exactly these.
 */

import { PRIMARY_STAT_KEYS, ZERO_PRIMARY_STATS, validatePrimaryStats, type PrimaryStats } from './primary-stats.js';
import type { AffinityRow, WeaponPrimaryProfile } from './weapon-primary-profile.js';
import { isFiniteNumber } from '../math.js';
import { SOURCE_CLASSES, WEAPON_FAMILIES, WEAPON_TECHNIQUES, type SourceRequirement, type WeaponFamily, type WeaponTechnique } from '../types.js';
import { compatibleWeaponFamilies } from './source-compatibility.js';
import { BALANCE_THRUST_GEOMETRY_V0 } from './basic-attack-geometry.js';
import { normalizeWeaponContactProfileId } from './weapon-contact.js';
import type { PlayableWeaponId, PrimaryStatV0WeaponId } from '../reference/weapon-progression-v0.js';
import type { CatalystSkillSplash } from './catalyst-basic.js';

export type { WeaponFamily, SourceRequirement } from '../types.js';

/** Minimum owner-scoped combat source seam (audit §G) — deliberately not a
 * full inventory/loot/equipment system. Individual weapon subtype owns its
 * own base numbers; the family owns the affinity model (contract §3.1). */
export interface CombatSourceDefinition {
  id: string;
  family: WeaponFamily;
  /**
   * Concrete equipped weapon identity used by canonical WEAPON_CONTACT.
   * Source-relative skills that do not borrow spatial geometry remain valid
   * without this additive metadata; a contact skill fails closed when it is
   * absent rather than guessing a profile from the source id.
   */
  weaponId?: PlayableWeaponId | PrimaryStatV0WeaponId;
  baseDamage: number;
  baseAttackRate: number;
}

export interface CombatSourceDefinitionValidationResult {
  ok: boolean;
  errors: string[];
}

export function validateCombatSourceDefinition(source: CombatSourceDefinition): CombatSourceDefinitionValidationResult {
  const errors: string[] = [];
  if (typeof source.id !== 'string' || source.id.length === 0) errors.push('CombatSourceDefinition.id must be a non-empty string');
  if (!WEAPON_FAMILIES.includes(source.family)) errors.push(`CombatSourceDefinition.family must be one of ${WEAPON_FAMILIES.join(', ')} (got ${String(source.family)})`);
  if (!isFiniteNumber(source.baseDamage) || source.baseDamage <= 0) errors.push(`CombatSourceDefinition.baseDamage must be finite > 0 (got ${source.baseDamage})`);
  if (!isFiniteNumber(source.baseAttackRate) || source.baseAttackRate <= 0) errors.push(`CombatSourceDefinition.baseAttackRate must be finite > 0 (got ${source.baseAttackRate})`);
  return { ok: errors.length === 0, errors };
}

/** Prefer an immutable result. */
export interface ResolvedCombatSourceProfile {
  readonly sourceId: string;
  readonly family: WeaponFamily;
  readonly weaponId?: PlayableWeaponId | PrimaryStatV0WeaponId;
  readonly hBase: number;
  readonly hStat: number;
  readonly hitDamage: number;
  readonly attackRatePerS: number;
  readonly cycleS: number;
  /**
   * SOURCE.2: source-relative action/recast
   * timing bases, split so intrinsic MAGIC may cast only slightly slower
   * while recovering substantially slower. Weapon resolution keeps both
   * equal to `cycleS` — `cycleS` itself is unchanged and still owns basic-
   * attack cadence; only `sourceRelativeActionLockS`/`sourceRelativeRecastS`
   * read `actionCycleS`/`recastCycleS` instead.
   */
  readonly actionCycleS: number;
  readonly recastCycleS: number;
  /** A held Blast Catalyst's splash on a crafted magic skill's direct hit; realized at cast, never priced. */
  readonly catalystSplash?: CatalystSkillSplash;
}

/**
 * SourceSnapshot: the frozen, per-activation
 * runtime fact set. Structurally identical to `ResolvedCombatSourceProfile`
 * — the distinction is lifecycle (created once at accepted activation start,
 * immutable for that activation, never authored on `Skill`), not shape.
 */
export type SourceSnapshot = ResolvedCombatSourceProfile;

/** The profile damage slope every skill stored before the single 3% slope was authored against. */
export const LEGACY_AUTHORING_DAMAGE_SLOPE = 0.003;

/**
 * F/K weight H_base and H_stat, so a skill authored at one damage slope and executed at another would change its
 * damage whenever F != K. This keeps the hit (and so cadence, which never reads the split) and re-splits it the way
 * the skill's authoring slope would have: the stat share `Q` is `hStat / (hBase x slope)`, and the hit
 * `hBase (1 + slope Q)` is divided again as `hBase' (1 + authored Q)`.
 */
export function withAuthoredDamageSplit(snapshot: SourceSnapshot, authoredSlope: number, profileSlope: number): SourceSnapshot {
  if (authoredSlope === profileSlope || snapshot.hBase <= 0 || profileSlope <= 0) return snapshot;
  const statShare = snapshot.hStat / (snapshot.hBase * profileSlope);
  const hBase = snapshot.hitDamage / (1 + authoredSlope * statShare);
  return Object.freeze({ ...snapshot, hBase, hStat: hBase * authoredSlope * statShare });
}

/** Q_D(f) / Q_R(f) — affinity score over the canonical stat order (work
 * instruction §76: never rely on object insertion order). */
function affinityScore(row: Readonly<AffinityRow>, primary: PrimaryStats): number {
  let score = 0;
  for (const key of PRIMARY_STAT_KEYS) {
    const weight = row[key];
    if (weight === undefined || weight === 0) continue;
    score += weight * primary[key];
  }
  return score;
}

/**
 * Source-profile resolution (contract §4/§25): H_base/H_stat stay separate
 * (F/K requires both) — never collapsed into one H.
 */
export function resolveSourceProfile(
  source: CombatSourceDefinition,
  primary: PrimaryStats,
  profile: WeaponPrimaryProfile,
): ResolvedCombatSourceProfile {
  const qD = affinityScore(profile.damageAffinity[source.family], primary);
  const qR = affinityScore(profile.handlingAffinity[source.family], primary);
  const hBase = source.baseDamage;
  const hStat = source.baseDamage * profile.damageSlope * qD;
  const attackRatePerS = source.baseAttackRate * (1 + profile.attackRateSlope * qR);
  const cycleS = 1 / attackRatePerS;
  return Object.freeze({
    sourceId: source.id,
    family: source.family,
    ...(source.weaponId === undefined ? {} : { weaponId: source.weaponId }),
    hBase,
    hStat,
    hitDamage: hBase + hStat,
    attackRatePerS,
    cycleS,
    // Weapon resolution: action and recast share the same cycle basis —
    // unchanged from pre-SOURCE.2 behavior.
    actionCycleS: cycleS,
    recastCycleS: cycleS,
  });
}

/**
 * SOURCE.2 — discriminated-union-aware validator.
 * Defensively probes fields at runtime rather than trusting the
 * static `SourceRequirement` type, since callers may pass malformed/
 * untyped input (existing pattern: `validateSourceRequirement({...} as
 * unknown as SourceRequirement)` in `test/source1-reference-basis-contract.test.ts`).
 * Unknown `sourceClass` fails closed.
 */
export function validateSourceRequirement(requirement: SourceRequirement): CombatSourceDefinitionValidationResult {
  const errors: string[] = [];
  const r = requirement as { sourceClass?: unknown; technique?: unknown; families?: unknown };
  if (!SOURCE_CLASSES.includes(r.sourceClass as (typeof SOURCE_CLASSES)[number])) {
    errors.push(`SourceRequirement.sourceClass must be one of ${SOURCE_CLASSES.join(', ')} (got ${String(r.sourceClass)})`);
    return { ok: false, errors };
  }
  if (r.sourceClass === 'BODY' || r.sourceClass === 'MAGIC') {
    if (r.technique !== undefined) errors.push(`SourceRequirement.sourceClass=${r.sourceClass} must not carry technique`);
    if (r.families !== undefined) errors.push(`SourceRequirement.sourceClass=${r.sourceClass} must not carry families`);
    return { ok: errors.length === 0, errors };
  }
  // sourceClass === 'WEAPON'
  if (!WEAPON_TECHNIQUES.includes(r.technique as (typeof WEAPON_TECHNIQUES)[number])) {
    errors.push(`SourceRequirement.technique must be one of ${WEAPON_TECHNIQUES.join(', ')} (got ${String(r.technique)})`);
  }
  if (r.families !== undefined) {
    if (!Array.isArray(r.families) || r.families.length === 0) {
      errors.push('SourceRequirement.families, when present, must be a non-empty array');
    } else {
      for (const family of r.families as unknown[]) {
        if (!WEAPON_FAMILIES.includes(family as WeaponFamily)) errors.push(`SourceRequirement.families contains an unknown family "${String(family)}"`);
      }
      if (new Set(r.families).size !== (r.families as unknown[]).length) errors.push('SourceRequirement.families must not contain duplicates');
    }
  }
  return { ok: errors.length === 0, errors };
}

/**
 * WEAPON-only legality: whether `source.family` is within the requirement's
 * effective legal set (technique-compatible families ∩ explicit restriction,
 * `compatibleWeaponFamilies`, contract §19). `BODY`/`MAGIC` requirements are
 * never legal through this weapon-equipped-source check — their runtime
 * resolution is owned by the BODY typed-defer boundary and
 * `resolveMagicSourceSnapshot` respectively (SOURCE.2 Tasks 4/6/7).
 */
export function isSourceLegal(source: CombatSourceDefinition, requirement: SourceRequirement): boolean {
  if (requirement.sourceClass !== 'WEAPON') return false;
  return compatibleWeaponFamilies(requirement).includes(source.family) && weaponCarriesTechnique(source, requirement.technique);
}

/**
 * Whether the equipped weapon itself — not just its family — has the shape for
 * the technique: a thrust needs a corridor (`BALANCE_THRUST_GEOMETRY_V0`), so a
 * heavy axe, in a family that may thrust, does not. The craft catalog offers an
 * action by this same rule; the check here is what keeps a saved skill, whatever
 * shape replaced its weapon contact, from casting with a weapon that lacks it.
 */
function weaponCarriesTechnique(source: CombatSourceDefinition, technique: WeaponTechnique): boolean {
  if (technique !== 'THRUST' || source.weaponId === undefined) return true;
  const profileId = normalizeWeaponContactProfileId(source.weaponId);
  return profileId === undefined || BALANCE_THRUST_GEOMETRY_V0[profileId] !== undefined;
}

/** F/K damage core (addendum §1): `D_core = F × H_base + K × H_stat`. No
 * extra multiplier — this is the entire formula. */
export function sourceRelativeDamageCore(f: number, k: number, snapshot: Pick<SourceSnapshot, 'hBase' | 'hStat'>): number {
  return f * snapshot.hBase + k * snapshot.hStat;
}

/**
 * `P_ref` (addendum §1.1): a derived-only diagnostic/evaluator projection.
 * Not authored power, not persisted as a competing authority, and — critically
 * — never multiplied at runtime (STOP §91: `P × (F×H_base + K×H_stat)` is a
 * blocker). Callers must not feed this back into `sourceRelativeDamageCore`.
 */
export function projectPRef(f: number, k: number, referenceSnapshot: Pick<SourceSnapshot, 'hBase' | 'hStat'>): number {
  const hRef = referenceSnapshot.hBase + referenceSnapshot.hStat;
  if (hRef <= 0) throw new Error('projectPRef: reference snapshot H_base + H_stat must be > 0');
  return sourceRelativeDamageCore(f, k, referenceSnapshot) / hRef;
}

/**
 * ACTIVE.1 activation-start seam (audit §F): the
 * single place `simulator.ts`/`authoritative-world.ts`'s `beginCast` resolves
 * the owner's current source into a frozen `SourceSnapshot`, *before* target
 * acquisition (so an illegal/missing source never consumes RANDOM targeting
 * RNG). Returns:
 *  - `undefined` — this skill declares no `sourceRequirement`: it draws no
 *    source-relative authority at all; legacy behavior, no gating.
 *  - `null` — the skill needs a source but none is equipped, or the equipped
 *    source's family is not in `requirement.families`: fail closed, the
 *    caller must not begin the cast (no cost/cooldown/target RNG).
 *  - a `SourceSnapshot` — resolved and ready to freeze onto `CastState`.
 * Missing `primaryStats` defaults to `ZERO_PRIMARY_STATS` (the zero-stat
 * invariant: an owner who has not yet allocated Primary points still gets a
 * functional source), never an implicit skill rejection.
 *
 * Also fails closed (`null`) when the owner's current `equippedSource`/
 * `primaryStats` is itself malformed (non-finite/negative fields) — an
 * existing validator not wired into this exact seam left a real gap: without
 * this, a malformed `baseAttackRate: 0` or `primaryStats.STR:
 * NaN` would otherwise propagate as `Infinity`/`NaN` into `D_core`/`cycleS`
 * rather than being rejected here, at the one activation-start gate every
 * source-relative cast must pass through.
 */
export function resolveCastSourceSnapshot(
  requirement: SourceRequirement | undefined,
  equippedSource: CombatSourceDefinition | undefined,
  primaryStats: PrimaryStats | undefined,
  profile: WeaponPrimaryProfile,
): SourceSnapshot | null | undefined {
  if (requirement === undefined) return undefined;
  if (equippedSource === undefined || !isSourceLegal(equippedSource, requirement)) return null;
  if (!validateCombatSourceDefinition(equippedSource).ok) return null;
  const effectivePrimary = primaryStats ?? ZERO_PRIMARY_STATS;
  if (!validatePrimaryStats(effectivePrimary).ok) return null;
  return resolveSourceProfile(equippedSource, effectivePrimary, profile);
}

/**
 * A/C timing core (contract §2, addendum §1; split by SOURCE.2 work
 * instruction §4.8): `actionLockS = A × T_action`, `recastS = C × T_recast`.
 * For weapon resolution `T_action === T_recast === cycleS` (unchanged); only
 * intrinsic MAGIC's resolver produces a snapshot where they diverge.
 */
export function sourceRelativeActionLockS(actionCycles: number, snapshot: Pick<SourceSnapshot, 'actionCycleS'>): number {
  return actionCycles * snapshot.actionCycleS;
}

export function sourceRelativeRecastS(recastCycles: number, snapshot: Pick<SourceSnapshot, 'recastCycleS'>): number {
  return recastCycles * snapshot.recastCycleS;
}
